import React, { useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import BottomSheetHandle from '../../atoms/BottomSheetHandle';

const DashboardFiltersDrawer = ({
  open,
  onClose,
  filters,
  setFilters,
  brandName,
  competitorNames,
  availableTags,      // Comes from useDashboardStats.tagStats
  availableKeywords,   // Comes from useDashboardKeywordsCloud.keywordsCloud
  analysisMode,
  onAnalysisModeChange,
  timePeriod,
  onTimePeriodChange,
  onOpenCustomPeriod
}) => {

  const getTagId = (tag) => tag?._id || tag?.id || String(tag);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      scope: '',
      category: '',
      uf: '',
      tags: [],
      competitor: '__brand__',
      keyword: ''
    });
  };

  // The true list of valid tags to render. (tagStats from DashboardStats yields objects with _id)
  // But availableTags might be objects or just IDs if we didn't populate them. 
  // we will map availableTags against contextTags in Dashboard.js, so what arrives here is an array of objects: [{_id, name}]
  
  const keywordOptions = availableKeywords?.map(k => k.text) || [];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Remove Entity names (Brand and Competitors) from the purely strategic Tags list
  // So that users don't accidentally select "Shopee" as a Tag when it should be an Entity.
  const validTags = useMemo(() => {
    if (!availableTags) return [];
    
    // Nomes proibidos de aparecerem como Tag:
    const forbiddenNames = [
      brandName?.toLowerCase(), 
      ...(competitorNames || []).map(n => n?.toLowerCase())
    ].filter(Boolean);

    return availableTags.filter(tag => {
      if (!tag.name) return false;
      const tName = tag.name.toLowerCase();
      // Se exatamente igual ao nome de um concorrente ou marca, removemos
      if (forbiddenNames.includes(tName)) return false;
      // Adicional: remover variações evidentes da marca (ex: "gi group holding" se brandName="gi group")
      if (brandName && tName.includes(brandName.toLowerCase())) return false;
      
      return true;
    });
  }, [availableTags, brandName, competitorNames]);

  const sectionSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    mb: 2
  };

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      variant="temporary"
      elevation={0}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400 },
          maxHeight: { xs: '88dvh', sm: '100%' },
          borderRadius: { xs: '18px 18px 0 0', sm: 0 },
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
          borderLeft: `1px solid ${theme.palette.divider}`,
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 4 },
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }
      }}
    >
      {isMobile && (
        <BottomSheetHandle sx={{ mt: -1, mb: -1 }} />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterIcon color="primary" /> Filtros Avançados
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: -2 }}>
        Hierarquia: Foco Global - Tags - Palavras
      </Typography>

      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" fontWeight={700} color="text.secondary">
          Contexto da análise
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel>Foco Global</InputLabel>
          <Select value={analysisMode} onChange={onAnalysisModeChange} label="Foco Global">
            <MenuItem value="brand">{brandName || 'Marca'}</MenuItem>
            <MenuItem value="competitors">Concorrentes</MenuItem>
            <MenuItem value="all">Tudo</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel>Período</InputLabel>
          <Select value={timePeriod} onChange={onTimePeriodChange} label="Período">
            <MenuItem value="1d">Último dia</MenuItem>
            <MenuItem value="3d">Últimos 3 dias</MenuItem>
            <MenuItem value="7d">Últimos 7 dias</MenuItem>
            <MenuItem value="30d">Últimos 30 dias</MenuItem>
            <MenuItem value="custom" onClick={onOpenCustomPeriod}>Personalizado...</MenuItem>
            <MenuItem value="all">Tudo</MenuItem>
          </Select>
        </FormControl>
        <Divider />
      </Box>

      {/* 2. SEÇÃO CASCATA: TAGS */}
      <Box sx={sectionSx}>
        <Typography variant="body2" fontWeight={600} color="text.secondary" gutterBottom>
          Classificadores (Tags)
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel>Tags Estratégicas</InputLabel>
          <Select
            multiple
            value={filters.tags || []}
            label="Tags Estratégicas"
            onChange={(e) => {
              handleFilterChange('tags', e.target.value);
              handleFilterChange('keyword', ''); // reset cascade
            }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const tagObj = availableTags?.find(t => getTagId(t) === value);
                  return (
                    <Chip 
                      key={value} 
                      label={tagObj ? tagObj.name : 'Tag'} 
                      size="small" 
                      onDelete={() => {
                        const newTags = filters.tags.filter(t => t !== value);
                        handleFilterChange('tags', newTags);
                      }}
                      onMouseDown={(e) => {
                        // Evita que o Select abra ao clicar no 'x' do Chip
                        e.stopPropagation();
                      }}
                    />
                  );
                })}
              </Box>
            )}
          >
            {validTags.length === 0 && <MenuItem disabled>Nenhuma tag nesta seleção</MenuItem>}
            {validTags.map((tag) => (
              <MenuItem key={getTagId(tag)} value={getTagId(tag)}>
                {tag.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* 3. SEÇÃO CASCATA: PALAVRAS */}
      <Box sx={sectionSx}>
        <Typography variant="body2" fontWeight={600} color="text.secondary" gutterBottom>
          Filtro Específico (Palavras)
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel>Palavra-Chave Originária</InputLabel>
          <Select
            value={filters.keyword || ''}
            label="Palavra-Chave Originária"
            onChange={(e) => handleFilterChange('keyword', e.target.value)}
          >
            <MenuItem value="">Nenhuma (Todas)</MenuItem>
            {keywordOptions.length === 0 && <MenuItem disabled>Nenhuma palavra encontrada</MenuItem>}
            {keywordOptions.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* 4. SEÇÃO: METADOS AUXILIARES */}
      <Box sx={sectionSx}>
        <Typography variant="body2" fontWeight={600} color="text.secondary" gutterBottom>
          Divisões Gerais
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel>Abrangência</InputLabel>
          <Select
            value={filters.scope}
            label="Abrangência"
            onChange={(e) => handleFilterChange('scope', e.target.value)}
          >
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="Nacional">Nacional</MenuItem>
            <MenuItem value="Regional">Regional</MenuItem>
            <MenuItem value="Local">Local</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel>Categoria</InputLabel>
          <Select
            value={filters.category}
            label="Categoria"
            onChange={(e) => handleFilterChange('category', e.target.value)}
          >
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="Notícia">Notícia</MenuItem>
            <MenuItem value="Blog">Blog</MenuItem>
            <MenuItem value="Jornal">Jornal</MenuItem>
            <MenuItem value="Revista">Revista</MenuItem>
            <MenuItem value="TV">TV</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel>UF</InputLabel>
          <Select
            value={filters.uf}
            label="UF"
            onChange={(e) => handleFilterChange('uf', e.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="SP">SP</MenuItem>
            <MenuItem value="RJ">RJ</MenuItem>
            <MenuItem value="MG">MG</MenuItem>
            <MenuItem value="DF">DF</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mt: 'auto', pt: 2, pb: 1, display: 'flex', gap: 2 }}>
        <Button variant="outlined" color="inherit" fullWidth onClick={clearFilters}>
          Limpar Filtros
        </Button>
        <Button variant="contained" color="primary" fullWidth onClick={onClose}>
          Aplicar Análise
        </Button>
      </Box>

    </Drawer>
  );
};

export default DashboardFiltersDrawer;
